<template>
  <div v-if="renderedHtml" v-html="renderedHtml" />
  <slot v-else-if="status !== 'loading' && status !== 'visible'" />
</template>

<script setup lang="ts">
import { marked } from 'marked'
import type { TypeVisibilitySettingsWithoutUnresolvableLinksResponse } from '~/types/contentful/TypeVisibilitySettings'
import type { CustomElementsDependencies } from '~/types/custom-elements'

interface ContentfulEntry {
  sys?: {
    id?: string
    contentType?: {
      sys?: {
        id?: string
      }
    }
  }
  fields?: Record<string, unknown>
}

interface NuxtWindow extends Window {
  __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
}

const props = defineProps<{
  entry?: string
  debug?: boolean
}>()

const status = ref<'loading' | 'visible' | 'hidden' | 'unknown' | 'error'>(
  'loading',
)
const renderedHtml = ref('')
const cachedPayload = shallowRef<ContentfulEntry | null>(null)
const cachedLocale = ref('')
const lastSignature = ref('')

let pollTimer: number | undefined
let isRefreshing = false

const debugLog = (...args: unknown[]) => {
  if (props.debug) {
    console.log('[gated-content]', ...args)
  }
}

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const readCookie = (name: string): string => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${escapeRegex(name)}=([^;]*)`),
  )
  return match ? decodeURIComponent(match[1]) : ''
}

const getLocaleFromPath = (): string => {
  const match = window.location.pathname.match(
    /^\/([A-Za-z]{2}(?:-[A-Za-z]{2,8})?)(?:\/|$)/,
  )
  return match?.[1] || 'en'
}

const isResolvedEntry = (value: unknown): value is ContentfulEntry =>
  Boolean(
    value && typeof value === 'object' && (value as ContentfulEntry).fields,
  )

const hasUnresolvedEntryLinks = (value: unknown): boolean =>
  Array.isArray(value) && value.some((item) => !isResolvedEntry(item))

const extractMarkdown = (
  fields: Record<string, unknown> | undefined,
): string => {
  if (!fields) return ''

  const candidateKeys = ['body', 'copy', 'content', 'markdown', 'text']

  for (const key of candidateKeys) {
    const value = fields[key]

    if (typeof value === 'string' && value.trim()) {
      return value
    }

    if (value && typeof value === 'object') {
      const nestedBody = (value as { body?: unknown }).body
      if (typeof nestedBody === 'string' && nestedBody.trim()) {
        return nestedBody
      }
    }
  }

  return ''
}

const fetchEntry = async (
  entryId: string,
  locale: string,
  client: CustomElementsDependencies['client'],
): Promise<ContentfulEntry | null> => {
  const response = await client.getEntries({
    include: 10,
    limit: 1,
    locale,
    'sys.id': entryId,
  })

  return (response.items[0] as unknown as ContentfulEntry) || null
}

const refresh = async ({
  forceFetch = false,
}: { forceFetch?: boolean } = {}) => {
  if (isRefreshing) return
  isRefreshing = true

  try {
    const entryId = props.entry?.trim()

    if (!entryId) {
      status.value = 'hidden'
      renderedHtml.value = ''
      return
    }

    const deps = (window as NuxtWindow).__WPN_CUSTOM_ELEMENTS__
    if (!deps) {
      status.value = 'unknown'
      renderedHtml.value = ''
      debugLog('custom element dependencies are unavailable')
      return
    }

    const locale = deps.getLocale()

    const shouldFetch =
      forceFetch ||
      !cachedPayload.value ||
      cachedLocale.value !== locale ||
      cachedPayload.value?.sys?.id !== entryId

    if (shouldFetch) {
      cachedPayload.value = await fetchEntry(entryId, locale, deps.client)
      cachedLocale.value = locale
    }

    const payload = cachedPayload.value
    if (!payload) {
      status.value = 'error'
      renderedHtml.value = ''
      return
    }

    const visibilitySettings = payload.fields?.visibility

    // Missing visibility means unrestricted. A configured but unresolved link
    // fails conservatively instead of rendering publicly.
    if (visibilitySettings && !isResolvedEntry(visibilitySettings)) {
      status.value = 'unknown'
      renderedHtml.value = ''
      debugLog('visibility settings are unresolved')
      return
    }

    if (
      isResolvedEntry(visibilitySettings) &&
      (hasUnresolvedEntryLinks(visibilitySettings.fields?.countries) ||
        hasUnresolvedEntryLinks(visibilitySettings.fields?.languages))
    ) {
      status.value = 'unknown'
      renderedHtml.value = ''
      debugLog('visibility rules contain unresolved links')
      return
    }

    const decision = deps.visibilityDecision(
      visibilitySettings as
        | TypeVisibilitySettingsWithoutUnresolvableLinksResponse
        | undefined,
      { debug: props.debug },
    )
    debugLog('visibility decision', decision)

    if (decision.visible === null) {
      status.value = 'unknown'
      renderedHtml.value = ''
      return
    }

    if (!decision.visible) {
      status.value = 'hidden'
      renderedHtml.value = ''
      return
    }

    const markdown = extractMarkdown(payload.fields)
    renderedHtml.value = markdown ? String(marked.parse(markdown)) : ''
    status.value = 'visible'
  } catch (error) {
    status.value = 'error'
    renderedHtml.value = ''
    debugLog('unexpected error', error)
  } finally {
    isRefreshing = false
  }
}

const maybeRefreshOnSignatureChange = () => {
  const nextSignature = `${getLocaleFromPath()}|${readCookie('visitor_country').trim()}`
  if (nextSignature === lastSignature.value) return

  lastSignature.value = nextSignature
  void refresh()
}

const onPreferencesUpdated = () => {
  lastSignature.value = `${getLocaleFromPath()}|${readCookie('visitor_country').trim()}`
  void refresh()
}

watch(
  () => props.entry,
  (nextEntry, previousEntry) => {
    if (nextEntry !== previousEntry) {
      cachedPayload.value = null
      cachedLocale.value = ''
    }
    void refresh({ forceFetch: true })
  },
  { immediate: true },
)

onMounted(() => {
  lastSignature.value = `${getLocaleFromPath()}|${readCookie('visitor_country').trim()}`

  window.addEventListener(
    'wpn:visitor-preferences-updated',
    onPreferencesUpdated,
  )
  window.addEventListener('focus', maybeRefreshOnSignatureChange)

  pollTimer = window.setInterval(() => {
    maybeRefreshOnSignatureChange()
  }, 1500)
})

onBeforeUnmount(() => {
  window.removeEventListener(
    'wpn:visitor-preferences-updated',
    onPreferencesUpdated,
  )
  window.removeEventListener('focus', maybeRefreshOnSignatureChange)

  if (pollTimer) {
    window.clearInterval(pollTimer)
    pollTimer = undefined
  }
})
</script>
